import { ConfigApi, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { SearchApi } from '@backstage/plugin-search-react';
import { SearchQuery, SearchResultSet } from '@backstage/plugin-search-common';
import qs from 'qs';
import { SEARCH_TYPES } from '../utils/search.constant';
import {
  QualitySealStatus,
  qualitySealNotAllowedEntities,
} from '@internal/backstage-plugin-quality-seal-common';

type QualitySealStatusByKind = {
  entityRef: string;
  status: QualitySealStatus;
};

const getLocationPathname = (location: string): string | undefined => {
  try {
    return new URL(location, 'http://backstage.local').pathname;
  } catch {
    return undefined;
  }
};

const getSearchResultEntityRef = (document: Record<string, any>) => {
  if (typeof document.authorization?.resourceRef === 'string') {
    return document.authorization.resourceRef.toLowerCase();
  }

  if (typeof document.entityRef === 'string') {
    return document.entityRef.toLowerCase();
  }

  if (!document.kind) {
    return undefined;
  }

  const location = document.location;
  let name = document.name ?? document.metadata?.name;
  let pathNamespace: string | undefined;
  if (typeof location === 'string') {
    const pathname = getLocationPathname(location);
    const pathSegments = pathname?.split('/').filter(Boolean) ?? [];
    const catalogPathIndex = pathSegments.lastIndexOf('catalog');
    if (catalogPathIndex >= 0) {
      pathNamespace = pathSegments[catalogPathIndex + 1];
      if (!name) {
        name = pathSegments[catalogPathIndex + 3];
      }
    } else if (!name) {
      name = pathSegments[pathSegments.length - 1];
    }

    if (!document.name && !document.metadata?.name && name) {
      try {
        name = decodeURIComponent(name);
        if (pathNamespace) {
          pathNamespace = decodeURIComponent(pathNamespace);
        }
      } catch {
        return undefined;
      }
    }
  }

  if (!name) {
    return undefined;
  }

  const namespace =
    document.namespace ?? document.metadata?.namespace ?? pathNamespace;
  return `${document.kind}:${namespace || 'default'}/${name}`.toLowerCase();
};

const isCatalogResult = (item: SearchResultSet['results'][number]) => {
  const document = item.document as Record<string, any>;
  return (
    item.type === SEARCH_TYPES.CATALOG ||
    (Boolean(document.kind) &&
      typeof document.location === 'string' &&
      getLocationPathname(document.location)?.split('/').includes('catalog'))
  );
};

export class SearchClient implements SearchApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly isQualitySealEnabled: boolean;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    configApi: ConfigApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.isQualitySealEnabled =
      options.configApi.getOptionalBoolean('features.enable-quality-seal') ??
      false;
  }

  /** The Confluence shard types the search backend currently serves */
  async fetchConfluenceShards(): Promise<string[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('search');
    const response = await this.fetchApi.fetch(`${baseUrl}/confluence/types`);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data = await response.json();
    return data.types || [];
  }

  async query(query: SearchQuery): Promise<SearchResultSet> {
    // Resolved alongside the search request so enrichment adds no extra
    // discovery hop; a failure only drops the Quality Seal badges.
    const qualitySealBaseUrl = this.isQualitySealEnabled
      ? this.discoveryApi.getBaseUrl('quality-seal').catch(() => undefined)
      : undefined;
    const queryClone: SearchQuery = { ...query };
    if (queryClone.term) {
      queryClone.term = queryClone.term.replace(/^https?:\/\//i, '');
    }

    if (queryClone.types?.includes(SEARCH_TYPES.CONFLUENCE)) {
      queryClone.types = queryClone.types.filter(
        t => t !== SEARCH_TYPES.CONFLUENCE,
      );
      const shardTypes = await this.fetchConfluenceShards();
      if (shardTypes.length === 0) {
        return { results: [] };
      }
      queryClone.types.push(...shardTypes);
    }
    const queryString = qs.stringify(queryClone);
    const baseUrl = await this.discoveryApi.getBaseUrl('search');
    const url = `${baseUrl}/query?${queryString}`;
    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const result: SearchResultSet = await response.json();
    result.results =
      result.results?.map(r => ({
        ...r,
        type: r.type?.includes(SEARCH_TYPES.CONFLUENCE)
          ? SEARCH_TYPES.CONFLUENCE
          : r.type,
      })) || [];

    if (qualitySealBaseUrl) {
      await this.addQualitySealStatuses(result, qualitySealBaseUrl);
    }

    return result;
  }

  private async addQualitySealStatuses(
    result: SearchResultSet,
    qualitySealBaseUrl: Promise<string | undefined>,
  ): Promise<void> {
    const eligibleResults: Array<{
      document: Record<string, any>;
      entityRef: string;
    }> = [];
    for (const item of result.results) {
      if (!isCatalogResult(item)) {
        continue;
      }
      const document = item.document as Record<string, any>;
      const entityRef = getSearchResultEntityRef(document);
      const kind = entityRef?.split(':', 1)[0];
      if (entityRef && kind && !qualitySealNotAllowedEntities.includes(kind)) {
        eligibleResults.push({ document, entityRef });
      }
    }

    if (eligibleResults.length === 0) {
      return;
    }

    const entityRefs = [
      ...new Set(eligibleResults.map(({ entityRef }) => entityRef)),
    ];

    try {
      const baseUrl = await qualitySealBaseUrl;
      if (!baseUrl) {
        return;
      }
      const response = await this.fetchApi.fetch(`${baseUrl}/by-refs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityRefs }),
      });
      if (!response.ok) {
        return;
      }

      const statuses: QualitySealStatusByKind[] = await response.json();
      const statusByEntityRef = new Map(
        statuses.map(({ entityRef, status }) => [
          entityRef.toLowerCase(),
          status,
        ]),
      );

      eligibleResults.forEach(({ document, entityRef }) => {
        document.qualitySealStatus = statusByEntityRef.get(entityRef);
      });
    } catch {
      // Search results remain usable when Quality Seal is unavailable.
    }
  }
}
